import React, { useMemo, useState, useCallback } from 'react';
import { endOfWeek } from 'date-fns';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import ResourceTable from '../../../table/components/resource/ResourceTable';
import {
    CONTACT_RESOURCE,
    CONTRACT_RESOURCE,
    CUSTOMER_RESOURCE,
    INVOICE_RESOURCE,
    SUPPORT_RESOURCE,
} from '../../../api/resources';
import CustomerLink, { CONTRACTS_TAB } from '../../../customers/components/CustomerLink';
import CustomerType from '../../../customers/components/CustomerType';
import ContactWithTypeInfo from '../../../contacts/components/ContactWithTypeInfo';
import {
    CONTRACTING_PARTNER_CONTACT,
    INVOICE_ADDRESS_CONTACT,
    PATIENT_CONTACT,
} from '../../../contacts/hooks';
import Location from '../../../locations/components/Location';
import DisplayDate from '../../../date/components/DisplayDate';
import { DATETIME_FORMAT } from '../../../date/utils';
import Currency from '../../../currencies/components/Currency';
import AccountingButton from '../../../buttons/components/AccountingButton';
import { useDialog } from '../../../dialogs/components/DialogContext';
import AccountingIconButton from '../../../buttons/components/AccountingIconButton';
import ContractTypeButton from '../../../buttons/components/ContractTypeButton';
import InvoiceDetails from './InvoiceDetails';
import { useNoteAction } from '../../../notes/hooks';
import { updateCustomer } from '../../../customers/customersSlice';
import TextInput from '../../../form/components/TextInput';
import { PZH_CONTRACT } from '../../../contracts/contractTypes';
import { useCanWrite } from '../../../abilities/hooks';
import TitleBar from '../../../layout/components/TitleBar';
import ExportInvoiceData from './ExportInvoiceData';
import SyncCrmCustomerWithDatev from './SyncCrmCustomerWithDatev';
import { exportDataRequest, syncCustomerDataRequest } from '../../../../Components/Export/hooks';
import ExportedInfoButton from 'Modules/buttons/components/ExportedInfoButton';
import CompletedButton from 'Modules/buttons/components/CompletedButton';
import RefreshIconButton from 'Modules/buttons/components/RefreshIconButton';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Alert from '@material-ui/lab/Alert';

import {
    updateInvoice,
} from '../../../invoices/invoicesSlice';

const EXPORT_TYPE = 'pzhcontractinvoice';

const NOT_COMPLETED = 'NULL';
const COMPLETED = 'NOT_NULL';

const LIST_ID = 'support.invoices';
const baseColumns = [
    {
        key: 'customerId',
        related: 'contract',
        label: 'Nr.',
        accessor: 'customerId',
        as: 'customerId',
        component: CustomerLink,
        showId: true,
        tab: CONTRACTS_TAB,
    },
    {
        key: 'status',
        accessor: 'customerId',
        related: 'contract',
        label: 'Status',
        component: CustomerType,
        sortable: false,
    },
    {
        key: 'contractingPartner',
        accessor: 'customerId',
        related: 'contract',
        label: 'Vertragspartner',
        component: ContactWithTypeInfo,
        type: CONTRACTING_PARTNER_CONTACT,
        sortable: false,
    },
    {
        key: 'invoiceAddress',
        accessor: 'customerId',
        related: 'contract',
        label: 'Rechnungsversandadresse',
        component: ContactWithTypeInfo,
        type: INVOICE_ADDRESS_CONTACT,
        withPhone: true,
        withEmail: true,
        sortable: false,
    },
    {
        key: 'patientAddress',
        accessor: 'customerId',
        related: 'contract',
        label: 'Zu betreuende Person',
        component: ContactWithTypeInfo,
        type: PATIENT_CONTACT,
        sortable: false,
    },
    {
        key: 'locationId',
        related: 'customer',
        label: 'Standort',
        component: Location,
        sortable: false,
    },
    {
        key: 'startAt',
        related: 'contract',
        as: 'value',
        label: 'Vertragsbeginn',
        component: DisplayDate,
        sortable: true,
    },
    {
        key: 'endAt',
        related: 'contract',
        as: 'value',
        label: 'Vertragsende',
        component: DisplayDate,
        sortable: false,
    },
    {
        key: 'amount',
        label: 'Zu berechnen',
        as: 'value',
        component: Currency,
    },
    // {
    //     key: 'accountingRecordedFrom',
    //     related: 'customer',
    //     label: 'BuHa. erfasst',
    //     as: 'value',
    //     component: DisplayDate,
    // },
];

// Only relevant once an invoice is completed (and can be reverted), so the
// user sees when it was exported to Datev without having to open the row.
const exportedToDatevAtColumn = {
    key: 'exportedToDatevAt',
    label: 'Exportiert am',
    as: 'value',
    component: DisplayDate,
    showTime: true,
    sortable: true,
};

const fields = [
    {
        name: 'customer.accountingNotes',
        label: 'Notizen zur Abrechnung des Kunden',
        component: TextInput,
        rows: 8,
        rowsMax: 30,
        multiline: true,
        fullWidth: true,
    },
];

const Invoices = () => {
    const { openAccountingRecordedDialog } = useDialog();
    const dispatch = useDispatch();

    const [reloadKey, setReloadKey] = useState(0);
    const [billedAtFilter, setBilledAtFilter] = useState(NOT_COMPLETED);

    const [isLoadingExportData, setIsLoadingExportData] = useState(false);
    const [isSyncingCustomerData, setIsSyncingCustomerData] = useState(false);

    const handleRefresh = useCallback(() => setReloadKey((prev) => prev + 1), []);

    const onExport = async (values) =>{
        //console.log('Exporting values =>', values);
        //setErrorLoadingExportData(null);

        try {
            setIsLoadingExportData(true);
            await exportDataRequest(EXPORT_TYPE, null);
        } catch (err) {
            //setErrorLoadingExportData(err);
        } finally {
            setIsLoadingExportData(false);

            // trigger a reload of the invoices
            setReloadKey(prev => prev + 1);
        }
    };

    const poll = async (fn, validate, interval, maxAttempts) => {
        let attempts = 0;

        const executePoll = async (resolve, reject) => {
            try {
                const result = await fn();
                attempts++;

                if (validate(result)) {
                    return resolve(result);
                } else if (maxAttempts && attempts >= maxAttempts) {
                    return reject(new Error('Exceeded max polling attempts for sync job.'));
                } else {
                    setTimeout(executePoll, interval, resolve, reject);
                }
            } catch (error) {
                return reject(error);
            }
        };

        return new Promise(executePoll);
    };

    const onSyncCustomerData = async (values) => {
        try {
            console.log("onSyncCustomerData");
            setIsSyncingCustomerData(true);

            // 1. Start the job. Backend should return a job_id.
            const { job_id } = await syncCustomerDataRequest({
                type: "startSyncAllDueInvoiceCustomer",
            });

            // 2. Poll for job status every 5 seconds for up to 15 minutes.
            const interval_s = 5000; // 5-second interval (5000ms)
            const max_attempts = (1000 * 60 * 15) / interval_s; // 15-minute timeout
            console.log("poll for job status interval => ",interval_s, " max_attemps => ", max_attempts);
            await poll(
                () => syncCustomerDataRequest({ type: 'getSyncJobStatus', job_id: job_id }),
                (result) => result?.status === 'completed' || result?.status === 'failed',
                interval_s, 
                max_attempts
            );
            console.log('Customer data sync completed.');
        } catch (err) {
            console.error("Customer data sync failed:", err);
            // TODO: Show an error notification to the user.
        } finally {
            setIsSyncingCustomerData(false);

            // trigger a reload of the invoices
            setReloadKey(prev => prev + 1);
        }
    }

    const handleExportedInfoClick = useCallback(({customer, invoice }) => {
        if (!invoice || !invoice.id) {
            console.error('No invoice data available for export action');
            return;
        }

        if (!customer?.datevId) {
            console.error('Customer is not synced with Datev. Export not possible.');
            return;
        }

        // Toggle the exportedToDatevAt field
        const updatedInvoice = {
            ...invoice,
            exportedToDatevAt: invoice.exportedToDatevAt ? null : moment().format(DATETIME_FORMAT),
        };

        dispatch(updateInvoice({ id: invoice.id, ...updatedInvoice }));
        console.log('Updated invoice #', updatedInvoice.id,' with exportedToDatevAt:', updatedInvoice.exportedToDatevAt);
    }, [dispatch]);

    const handleCompletedClick = useCallback(({customer, invoice }) => {
        if (!invoice || !invoice.id) {  
            console.error('No invoice data available for completed action');
            return;
        }

        if (customer?.datevId !== null &&  !invoice?.exportedToDatevAt) {
            console.error('Invoice not exported to Datev. Completion not possible.');
            return;
        }
        // Toggle the billedAt field
        const updatedInvoice = {
            ...invoice,
            billedAt: invoice.billedAt ? null : moment().format(DATETIME_FORMAT),
        };
        dispatch(updateInvoice({ id: invoice.id, ...updatedInvoice }));
        console.log('Updated invoice #', updatedInvoice.id,' with billedAt:', updatedInvoice.billedAt);

        // trigger a reload of the invoices
        setReloadKey(prev => prev + 1);
    }, [dispatch, setReloadKey]);

    const noteAction = useNoteAction({
        fields,
        getInitialValues: ({ related: { customer } }) => ({
            customer: {
                accountingNotes: (customer && customer.accountingNotes) || '',
            },
        }),
        onSubmit: (values, { related: {  customer } }) => {
            return Promise.all([
                dispatch(updateCustomer({ id: customer.id, ...values.customer })),
            ]);
        },
        hasNotes: ({ related: { customer } }) =>
            (customer && customer.accountingNotes),
    });
    const columns = useMemo(
        () => (billedAtFilter === COMPLETED ? [...baseColumns, exportedToDatevAtColumn] : baseColumns),
        [billedAtFilter]
    );

    const canWrite = useCanWrite(SUPPORT_RESOURCE);
    const actions = useMemo(() => {
        const allActions = canWrite
            ? [
                  {
                      key: 'contract_type',
                      action: ({ related: { contract } }) => (
                          <ContractTypeButton
                              label={contract?.contractType}
                              size="small"
                              color="primary"
                          />
                      ),
                      compact: ({ related: { contract } }) => (
                          <ContractTypeButton
                              label={contract?.contractType}
                              size="small"
                              color="primary"
                          />
                      ),
                  },
                  {
                      key: 'accounting',
                      action: ({ related: { customer } }) => (
                          <AccountingButton
                              onClick={() =>
                                  openAccountingRecordedDialog({ customerId: customer.id })
                              }
                              disabled={!!customer?.accountingRecordedFrom || customer?.datevId!== null}
                          >
                              BuHa. erfasst
                          </AccountingButton>
                      ),
                      compact: ({ related: { customer } }) => (
                          <AccountingIconButton
                              onClick={() =>
                                  openAccountingRecordedDialog({ customerId: customer.id })
                              }
                              size="small"
                              disabled={!!customer?.accountingRecordedFrom}
                              color="primary"
                          />
                      ),
                  },
                  noteAction,
                {
                    key: 'exported_info',
                    action: ({ data: invoice , related: { customer }  }) => (
                        <ExportedInfoButton
                            onClick={() =>
                                handleExportedInfoClick({customer, invoice })
                            }
                            size="small"
                            color="primary"
                            canBeExported={customer?.datevId !== null}
                            isexported={invoice.exportedToDatevAt !== null}
                        />
                    ),
                    compact: ({ data: invoice , related: { customer } }) => (
                        <ExportedInfoButton
                            onClick={() =>
                                handleExportedInfoClick({customer, invoice })
                            }
                            size="small"
                            color="primary"
                            canBeExported={customer?.datevId !== null}
                            isexported={invoice.exportedToDatevAt !== null}
                        />
                    ),
                },
                {
                    key: 'completed_info',
                    action: ({ data: invoice , related: { customer }  }) => (
                        <CompletedButton
                            onClick={() =>
                                handleCompletedClick({customer, invoice })
                            }
                            size="small"
                            color="primary"
                            canBeCompleted={
                                invoice.billedAt !== null ||
                                (customer?.datevId !== null && invoice.exportedToDatevAt !== null) ||
                                customer?.datevId === null
                            }
                            completed={invoice.billedAt !== null}
                            revert={billedAtFilter === COMPLETED}
                        />
                    ),
                    compact: ({ data: invoice , related: { customer } }) => (
                        <CompletedButton
                            onClick={() =>
                                handleCompletedClick({customer, invoice })
                            }
                            size="small"
                            color="primary"
                            canBeCompleted={
                                invoice.billedAt !== null ||
                                (customer?.datevId !== null && invoice.exportedToDatevAt !== null) ||
                                customer?.datevId === null
                            }
                            completed={invoice.billedAt !== null}
                            revert={billedAtFilter === COMPLETED}
                        />
                    ),
                },
              ]
            : [];

        // Once an invoice is completed, only the checker that reverts it
        // (setting billedAt back to null) is relevant here.
        return billedAtFilter === COMPLETED
            ? allActions.filter((a) => a.key === 'completed_info')
            : allActions;
    }, [
        openAccountingRecordedDialog,
        noteAction,
        canWrite,
        handleExportedInfoClick,
        handleCompletedClick,
        billedAtFilter,
    ]);

    return (
        <>

            <TitleBar>Abrechnung</TitleBar>
            <Grid item>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Alle Kundendaten werden jeden Morgen um 3:00 Uhr mit DATEV synchronisiert. Bis dahin sind 'neue/geänderte' Kundendaten in dieser Liste nicht zu 100% synchron zu denen auf dem DATEV Server!
                        </Alert>
                    </Grid>
                    <Grid item>
                        <ExportInvoiceData 
                            onExport={onExport}
                            isLoadingExportData={isLoadingExportData}
                        />
                    </Grid>
                    <Grid item>
                        <SyncCrmCustomerWithDatev
                            onSyncCustomerData={onSyncCustomerData}
                            isSyncingCustomerData={isSyncingCustomerData}
                        />
                    </Grid>
                    <Grid item>
                        <FormControl size="small" variant="outlined">
                            <InputLabel id="invoices-billed-at-filter-label">Status</InputLabel>
                            <Select
                                labelId="invoices-billed-at-filter-label"
                                label="Status"
                                value={billedAtFilter}
                                onChange={(event) => setBilledAtFilter(event.target.value)}
                            >
                                <MenuItem value={NOT_COMPLETED}>Nicht abgeschlossen</MenuItem>
                                <MenuItem value={COMPLETED}>Abgeschlossen</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <RefreshIconButton onClick={handleRefresh} title="Liste neu laden" />
                    </Grid>
                </Grid>
            </Grid>

            <ResourceTable
                key={`${reloadKey}-${billedAtFilter}`}
                listId={`${LIST_ID}.${billedAtFilter}`}
                resource={INVOICE_RESOURCE}
                columns={columns}
                staticParams={{
                    billedAt: billedAtFilter,
                    dueAt: { to: endOfWeek(new Date(), { weekStartsOn: 1 }) },
                    contract__type: PZH_CONTRACT,
                    contract__completeness: 'complete',
                }}
                with={{
                    customerContract: {
                        resource: CONTRACT_RESOURCE,
                        as: 'contract',
                    },
                    'customerContract.customer': {
                        resource: CUSTOMER_RESOURCE,
                        intermediate: 'customerContract',
                        intermediateKey: 'contractId',
                        as: 'customer',
                    },
                    'customerContract.customer.contacts': {
                        resource: CONTACT_RESOURCE,
                        byKey: 'customerId',
                        intermediate: 'customerContract.customer',
                    },
                }}
                limit={10}
                autoload
                refresh={true}
                actions={actions}
                showActionsInline
                expand={InvoiceDetails}
            />
        </>
    );
};

Invoices.propTypes = {};

Invoices.defaultProps = {};

export default Invoices;
